'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import type { ChatTab } from '../hooks/useChatTabs';
import { useChatActivityOptional } from '../contexts/ChatActivityContext';

/**
 * Horizontal strip of open chats, shown above the conversation when the user
 * has turned chat tabs on.
 *
 * Every tab here has a MOUNTED pane behind it (see `ChatTabPane`), so switching
 * is instant and a background chat keeps streaming. The dot on a tab is the
 * live indicator: filled + pulsing while that chat's turn is running, a smaller
 * solid dot when a turn finished while you were looking somewhere else.
 *
 * Keyboard: ⌘/Ctrl+1…9 jumps to a tab, ⌘/Ctrl+Alt+←/→ walks the strip.
 * (⌘W is left alone — closing the browser tab is what the user expects there.)
 */

export interface ChatTabStripProps {
  tabs: ChatTab[];
  activeChatId: string | null;
  onActivate: (chatId: string) => void;
  onClose: (chatId: string) => void;
  onNewChat: () => void;
  /** Disables the "+" while a new chat is being created. */
  isCreatingChat?: boolean;
}

function TabActivityDot({ chatId }: { chatId: string }) {
  const activity = useChatActivityOptional();
  if (!activity) return null;

  if (activity.isChatStreaming(chatId)) {
    return (
      <span
        className="relative flex h-2 w-2 flex-shrink-0"
        role="status"
        aria-label="Meridian is still working on this chat"
      >
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
    );
  }

  if (activity.hasUnread(chatId)) {
    return (
      <span
        className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
        role="status"
        aria-label="New reply in this chat"
      />
    );
  }

  return null;
}

export function ChatTabStrip({
  tabs,
  activeChatId,
  onActivate,
  onClose,
  onNewChat,
  isCreatingChat = false,
}: ChatTabStripProps) {
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the current tab in view when it changes from outside the strip
  // (sidebar click, deep link, keyboard shortcut).
  useEffect(() => {
    if (!activeChatId) return;
    const el = stripRef.current?.querySelector<HTMLElement>(`[data-chat-tab="${CSS.escape(activeChatId)}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeChatId, tabs.length]);

  // Read tabs through a ref so the shortcut listener is bound once instead of
  // being torn down and rebound on every title/LRU change.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeChatIdRef = useRef(activeChatId);
  activeChatIdRef.current = activeChatId;

  const step = useCallback(
    (delta: number) => {
      const list = tabsRef.current;
      if (list.length === 0) return;
      const current = list.findIndex((tab) => tab.id === activeChatIdRef.current);
      const from = current === -1 ? 0 : current;
      const next = (from + delta + list.length) % list.length;
      onActivate(list[next].id);
    },
    [onActivate],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;

      if (event.altKey && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        event.preventDefault();
        step(event.key === 'ArrowRight' ? 1 : -1);
        return;
      }

      // ⌘/Ctrl + 1…9. Alt-less only, so it never eats the OS text shortcuts.
      if (!event.altKey && !event.shiftKey && /^[1-9]$/.test(event.key)) {
        const index = Number(event.key) - 1;
        const target = tabsRef.current[index];
        if (!target) return;
        event.preventDefault();
        onActivate(target.id);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, onActivate]);

  if (tabs.length === 0) return null;

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label="Open chats"
      className="flex flex-shrink-0 items-stretch gap-1 overflow-x-auto border-b border-border bg-card px-2 py-1.5"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeChatId;
        return (
          <div
            key={tab.id}
            data-chat-tab={tab.id}
            className={`group flex max-w-[14rem] flex-shrink-0 items-center gap-1.5 rounded-md border pl-2.5 pr-1 py-1 transition-colors ${
              isActive
                ? 'border-primary/40 bg-primary/10'
                : 'border-transparent bg-muted/40 hover:bg-muted'
            }`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onActivate(tab.id)}
              // Middle-click closes, the way every tab strip does.
              onAuxClick={(event) => {
                if (event.button !== 1) return;
                event.preventDefault();
                onClose(tab.id);
              }}
              title={tab.title}
              className="flex min-w-0 items-center gap-1.5 text-left"
            >
              <TabActivityDot chatId={tab.id} />
              <span
                className={`truncate text-xs ${
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                }`}
              >
                {tab.title}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onClose(tab.id)}
              aria-label={`Close ${tab.title}`}
              className="flex-shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground focus:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary group-hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={onNewChat}
        disabled={isCreatingChat}
        aria-label="New chat"
        title="New chat"
        className="flex flex-shrink-0 items-center rounded-md px-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
